const STAGING_STACK_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,11}[a-z0-9]$/;

export const SHARED_STAGING_STACK_NAME = "staging";
export const STAGING_AIRLOCK_BASE_URL = "https://airlock.staging-api.replo.app";
export const SHARED_STAGING_GIT_PROXY_PUBLIC_ORIGIN =
  "https://staging-git.replo.app";
export const LOCAL_GIT_PROXY_PUBLIC_ORIGIN = "http://localhost:8790";

// Production backend origins. Single source of truth for both the editor's URL
// resolvers (apps/replo-agent/src/v2/utils/env.ts) and the miniapp bedrock
// client (packages/bedrock-sdk/miniapp.ts), so the REPLO_LOCAL_PROD_DATA
// "local frontend -> prod backend" flow points every surface at the same host.
export const PRODUCTION_BEDROCK_INTERNAL_API_BASE_URL =
  "https://bedrock.api.replo.app";
export const PRODUCTION_AGENT_COORDINATOR_BASE_URL =
  "https://cloudflare-agent-coordinator-production.replo.workers.dev";
export const PRODUCTION_AIRLOCK_BASE_URL = "https://airlock.replo.app";
export const PRODUCTION_GIT_PROXY_PUBLIC_ORIGIN = "https://git.replo.app";

class StagingStackError extends Error {}

export function getStagingStackName({
  inputName,
  rawName,
}: {
  inputName: string;
  rawName: string | undefined;
}): string {
  const stackName = rawName?.trim() || SHARED_STAGING_STACK_NAME;
  if (!STAGING_STACK_NAME_PATTERN.test(stackName)) {
    throw new StagingStackError(
      `Invalid ${inputName} "${stackName}". Must be kebab-case, 2-13 chars, [a-z0-9-].`,
    );
  }
  if (stackName === "default") {
    throw new StagingStackError(
      `"default" is reserved. Use blank for shared staging or choose a named staging alias.`,
    );
  }
  return stackName;
}

export function isSharedStagingStack(stackName: string): boolean {
  return stackName === SHARED_STAGING_STACK_NAME;
}

export function getAgentCoordinatorWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "cloudflare-agent-coordinator-staging"
    : `cloudflare-agent-coordinator-${stackName}`;
}

export function getAgentCoordinatorBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `https://${getAgentCoordinatorWorkerName({ stackName })}.replo.workers.dev`;
}

export function getAirlockWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "airlock-staging"
    : `airlock-${stackName}`;
}

export function getAirlockBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? STAGING_AIRLOCK_BASE_URL
    : `https://${getAirlockWorkerName({ stackName })}.replo.workers.dev`;
}

export function getAirlockJwksUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `${getAirlockBaseUrl({ stackName })}/.well-known/jwks.json`;
}

export function getBedrockApiWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "bedrock-api-staging"
    : `bedrock-api-${stackName}`;
}

export function getBedrockApiBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "https://bedrock.staging-api.replo.app"
    : `https://${getBedrockApiWorkerName({ stackName })}.replo.workers.dev`;
}

export function getPublisherApiBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `https://${stackName}-api.aws.replointernal.com`;
}

export function getFilesWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "cloudflare-files-worker-staging"
    : `cloudflare-files-worker-${stackName}`;
}

export function getFilesBaseUrl({ stackName }: { stackName: string }): string {
  return isSharedStagingStack(stackName)
    ? "https://files-staging.replousercontent.com"
    : `https://${getFilesWorkerName({ stackName })}.replo.workers.dev`;
}

export function getGitProxyWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "git-proxy-staging"
    : `git-proxy-${stackName}`;
}

export function getGitProxyBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? SHARED_STAGING_GIT_PROXY_PUBLIC_ORIGIN
    : `https://${getGitProxyWorkerName({ stackName })}.replo.workers.dev`;
}

export function getSandboxProxyWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "sandbox-proxy-staging"
    : `sandbox-proxy-${stackName}`;
}

export function getSandboxProxyBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `https://${getSandboxProxyWorkerName({ stackName })}.replo.workers.dev`;
}
